// src/pages/AlertsPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { alertsAPI, companiesAPI } from '../services/api';
import { Card, SectionHeader, EmptyState, Button, Modal, Input } from '../components/ui';
import toast from 'react-hot-toast';
import { Bell, Settings, AlertTriangle, Plus, Trash2, Mail, Search } from 'lucide-react';
import { format, fromUnixTime } from 'date-fns';
import styles from './AlertsPage.module.css';

export default function AlertsPage() {
    const { companyId: jwtCompanyId, isGaafAdmin } = useAuth();
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [showSettings, setShowSettings] = useState(false);
    const [companies, setCompanies] = useState([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState('');

    useEffect(() => {
        if (!isGaafAdmin) return;
        companiesAPI.list().then(res => {
            const list = res.data.companies || [];
            setCompanies(list);
            if (list.length > 0) setSelectedCompanyId(list[0].companyId);
        }).catch(() => { });
    }, [isGaafAdmin]);

    const companyId = isGaafAdmin ? (selectedCompanyId || jwtCompanyId) : jwtCompanyId;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await alertsAPI.list({ companyId });
            setAlerts(res.data.alerts || []);
        } catch (_) { }
        setLoading(false);
    }, [companyId]);

    useEffect(() => { load(); }, [load]);

    const filtered = alerts.filter(a =>
        !search ||
        a.productName?.toLowerCase().includes(search.toLowerCase()) ||
        a.staffName?.toLowerCase().includes(search.toLowerCase()) ||
        a.alertType?.toLowerCase().includes(search.toLowerCase()) ||
        a.message?.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className={`${styles.page} animate-fadeIn`}>
            <SectionHeader
                title="Alerts"
                description="Low Stock Alerts and Inventory Anomaly Notifications"
                actions={
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        {isGaafAdmin && companies.length > 0 && (
                            <select value={selectedCompanyId} onChange={e => setSelectedCompanyId(e.target.value)}
                                style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--text-primary)', padding: '7px 12px', fontFamily: 'var(--font-body)', fontSize: '0.875rem', outline: 'none' }}>
                                {companies.map(c => <option key={c.companyId} value={c.companyId}>{c.name}</option>)}
                            </select>
                        )}
                        <Button variant="secondary" icon={<Settings size={15} />} onClick={() => setShowSettings(true)}>
                            Alert Settings
                        </Button>
                    </div>
                }
            />

            <div style={{ position: 'relative', maxWidth: 360, marginBottom: 16 }}>
                <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)', pointerEvents: 'none' }} />
                <input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search by Product, Type, Message…"
                    style={{ width: '100%', paddingLeft: 34, paddingRight: 12, paddingTop: 8, paddingBottom: 8, background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-lg)', color: 'var(--text-primary)', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box' }}
                />
            </div>

            {loading ? (
                <div className={styles.list}>{[1, 2, 3].map(i => <div key={i} className={styles.skeleton} />)}</div>
            ) : filtered.length === 0 ? (
                <EmptyState
                    icon={<Bell size={40} />}
                    title={search ? 'No Alerts match your search' : 'No Alerts'}
                    description={search ? 'Try a different Search Term.' : 'Alerts appear here when Inventory falls below Configured Thresholds. Configure Email Recipients in Alert Settings.'}
                    action={!search && <Button variant="secondary" icon={<Settings size={15} />} onClick={() => setShowSettings(true)}>Configure Alerts</Button>}
                />
            ) : (
                <div className={styles.list}>
                    {filtered.map(alert => <AlertCard key={alert.alertId || alert.eventId} alert={alert} />)}
                </div>
            )}

            <AlertSettingsModal open={showSettings} companyId={companyId} onClose={() => setShowSettings(false)}
                onSaved={() => { setShowSettings(false); toast.success('Alert Settings Saved'); load(); }} />
        </div>
    );
}

function AlertCard({ alert }) {
    const isLowStock = alert.alertType === 'LOW_STOCK' || alert.eventType === 'LOW_STOCK';
    const isAnomaly = alert.alertType === 'ZONE_ANOMALY' || alert.alertType === 'AFTER_HOURS' || alert.eventType === 'ANOMALY';
    return (
        <Card className={styles.alertCard}>
            <div className={styles.alertHeader}>
                <div className={`${styles.alertIconWrap} ${isAnomaly ? styles.iconAnomaly : styles.iconLowStock}`}>
                    <AlertTriangle size={16} />
                </div>
                <div className={styles.alertBody}>
                    <div className={styles.alertTitle}>{alert.productName || alert.staffName || 'Alert'}</div>
                    <div className={styles.alertMeta}>
                        {alert.timestamp ? format(fromUnixTime(alert.timestamp), 'MMM d, yyyy HH:mm') : '—'}
                        {(alert.alertType || alert.eventType) && <span className={styles.alertType}>{(alert.alertType || alert.eventType).replace(/_/g, ' ')}</span>}
                    </div>
                    <div className={styles.alertMessage}>{alert.message || 'Alert'}</div>
                </div>
            </div>
            {alert.previousStatus && (
                <div className={styles.alertDetail}>Status Changed: <strong>{alert.previousStatus}</strong> → <strong>{alert.alertType || alert.eventType}</strong></div>
            )}
        </Card>
    );
}

function EmailSection({ type, emails, label, description, onAdd, onRemove }) {
    const [value, setValue] = useState('');
    const handleAdd = () => { if (!value.trim()) return; onAdd(type, value.trim()); setValue(''); };
    return (
        <div className={styles.emailSection}>
            <div className={styles.emailSectionHeader}>
                <Mail size={14} />
                <div>
                    <div className={styles.emailSectionTitle}>{label}</div>
                    <div className={styles.emailSectionDesc}>{description}</div>
                </div>
            </div>
            <div className={styles.emailList}>
                {emails.map(email => (
                    <div key={email} className={styles.emailChip}>
                        <span>{email}</span>
                        <button onClick={() => onRemove(type, email)} className={styles.removeEmail}><Trash2 size={11} /></button>
                    </div>
                ))}
            </div>
            <div className={styles.addEmailRow}>
                <input type="email" className={styles.emailInput} placeholder="name@company.com" value={value}
                    onChange={e => setValue(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAdd()} />
                <button className={styles.addBtn} onClick={handleAdd}><Plus size={14} /></button>
            </div>
        </div>
    );
}

function AlertSettingsModal({ open, companyId, onClose, onSaved }) {
    const [lowStockEmails, setLowStockEmails] = useState([]);
    const [anomalyEmails, setAnomalyEmails] = useState([]);
    const [generalEmails, setGeneralEmails] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);

    useEffect(() => {
        if (!open) { setLowStockEmails([]); setAnomalyEmails([]); setGeneralEmails([]); return; }
        if (!companyId || companyId === 'gaafrfid_platform') return;
        setLowStockEmails([]); setAnomalyEmails([]); setGeneralEmails([]);
        setFetching(true);
        companiesAPI.get(companyId).then(res => {
            const ae = res.data.company?.alertEmails || {};
            setLowStockEmails(ae.lowStock || []);
            setAnomalyEmails(ae.anomaly || []);
            setGeneralEmails(ae.general || []);
        }).catch(() => { }).finally(() => setFetching(false));
    }, [open, companyId]);

    const removeEmail = (type, email) => {
        if (type === 'lowStock') setLowStockEmails(p => p.filter(e => e !== email));
        if (type === 'anomaly') setAnomalyEmails(p => p.filter(e => e !== email));
        if (type === 'general') setGeneralEmails(p => p.filter(e => e !== email));
    };
    const addEmail = (type, email) => {
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return toast.error('Invalid Email');
        if (type === 'lowStock') setLowStockEmails(p => [...p, email]);
        if (type === 'anomaly') setAnomalyEmails(p => [...p, email]);
        if (type === 'general') setGeneralEmails(p => [...p, email]);
    };
    const handleSave = async () => {
        setLoading(true);
        try {
            await alertsAPI.updateSettings({ companyId, alertEmails: { lowStock: lowStockEmails, anomaly: anomalyEmails, general: generalEmails } });
            onSaved();
        } catch (err) { toast.error(err?.response?.data?.message || 'Failed to Save Settings'); }
        setLoading(false);
    };

    return (
        <Modal open={open} onClose={onClose} title="Alert Settings" size="md">
            {fetching ? (
                <div style={{ padding: '32px 0', textAlign: 'center', color: 'var(--text-tertiary)' }}>Loading…</div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    <p style={{ fontSize: '0.875rem', color: 'var(--text-tertiary)' }}>Configure Email Recipients for each Alert type. Emails are stored in your Company Profile — Never hardcoded.</p>
                    <EmailSection type="lowStock" emails={lowStockEmails} label="Low Stock Alerts" description="Sent to Purchasing Department when stock falls Below Threshold" onAdd={addEmail} onRemove={removeEmail} />
                    <EmailSection type="anomaly" emails={anomalyEmails} label="Anomaly Alerts" description="Sent when Inventory Discrepancies or Unusual Activity is detected" onAdd={addEmail} onRemove={removeEmail} />
                    <EmailSection type="general" emails={generalEmails} label="General Notifications" description="Fallback for all other Alert types" onAdd={addEmail} onRemove={removeEmail} />
                    <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 8, borderTop: '1px solid var(--border-subtle)' }}>
                        <Button variant="secondary" onClick={onClose}>Cancel</Button>
                        <Button variant="primary" loading={loading} onClick={handleSave}>Save Settings</Button>
                    </div>
                </div>
            )}
        </Modal>
    );
}
